import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Messages } from '../Messages/Messages'
import Participants from '../Participants/Participants'
import SidebarHeader from './SidebarHeader'
import { SidebarModeType } from '../../types/types'
import { useZoomContext } from '../../../../context/ZoomContext'
import styled from 'styled-components'
import StudyPlanContent from './StudyPlanContent'
import { useContentSharing } from '../../../../context/ContentSharingContext'
import { Tip } from '../../../../components/Tip'
import MentorAI from '../Mentor/MentorAI'
import HelpRequestPanel from '../HelpRequestPanel/HelpRequestPanel'

const Sidebar = () => {
    const { t } = useTranslation('sidebar')
    const { sidebarMode, setSidebarMode, sidebarExpanded, setSidebarExpanded } = useZoomContext()
    const ref = useRef<HTMLDivElement>(null)
    const { isContentShared } = useContentSharing()

    // Add effect to handle mode changes
    useEffect(() => {
        // Reset expanded state when switching to non-expandable modes
        if (sidebarMode !== 'content' && sidebarMode !== 'mentor') {
            setSidebarExpanded(false)
        }
    }, [sidebarMode, setSidebarExpanded])

    // Add this effect to close the sidebar when content sharing stops
    useEffect(() => {
        // If sidebar is showing content but content is not being shared, close it
        if (sidebarMode === 'content' && !isContentShared) {
            setSidebarMode(null)
        }
    }, [sidebarMode, setSidebarMode, isContentShared])

    const handleToggleExpand = () => {
        setSidebarExpanded(prev => !prev)
    }

    const getTool = () => {
        if (sidebarMode === 'messages') return <Messages />
        if (sidebarMode === 'participants') return <Participants />
        if (sidebarMode === 'content')
            return <StudyPlanContent onExpand={setSidebarExpanded} isExpanded={sidebarExpanded} />
        if (sidebarMode === 'mentor') return <MentorAI />
        if (sidebarMode === 'help-request') return <HelpRequestPanel />
    }

    const handleCloseSidebar = () => {
        setSidebarMode(null)
    }

    return (
        <SidebarStyled ref={ref} sidebarMode={sidebarMode} $isExpanded={sidebarExpanded}>
            <SidebarHeader
                title={
                    sidebarMode === 'messages'
                        ? t('sidebarComp.messages')
                        : sidebarMode === 'participants'
                        ? t('sidebarComp.participants')
                        : sidebarMode === 'content'
                        ? t('sidebarComp.content')
                        : sidebarMode === 'mentor'
                        ? t('sidebarComp.mentor', 'Mentor AI')
                        : sidebarMode === 'help-request'
                        ? t('sidebarComp.helpRequest', 'Help Requests')
                        : ''
                }
                handleClick={handleCloseSidebar}
                showCloseButton={true}
                isContentMode={sidebarMode === 'content' || sidebarMode === 'mentor'}
                sidebarMode={sidebarMode}
                isExpanded={sidebarExpanded}
                onToggleExpand={handleToggleExpand}
            />
            <Tip id="expand-tooltip" content={sidebarExpanded ? t('content.collapseView') : t('content.expandView')} />
            <Tip id="call-mentor-tooltip" content={t('content.callMentor')} />
            <div className="bottom">{getTool()}</div>
        </SidebarStyled>
    )
}

export default Sidebar

const SidebarStyled = styled.div<{ sidebarMode: SidebarModeType; $isExpanded: boolean }>`
    font-family: 'Poppins', sans-serif;
    width: ${props => (props.$isExpanded ? '50vw' : '380px')};
    max-width: 650px;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 0px 20px 5px rgba(0, 55, 80, 0.15);
    background-color: ${props => props.theme.color.sidebar.background};
    backdrop-filter: blur(10px);
    border-radius: 20px;
    overflow: hidden;
    box-sizing: border-box;
    gap: 10px;
    transition: all 0.3s ease-in-out;
    transform-origin: right center;
    margin-left: auto;
    .bottom {
        display: flex;
        flex-direction: column;
        padding: 5px 23px 23px 23px;
        flex-grow: 1;
        overflow-y: scroll;
        /* Hide scrollbar but keep functionality */
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
        &::-webkit-scrollbar {
            display: none; /* Chrome, Safari and Opera */
        }
    }
`
